import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { message } from 'antd'
import { defaultTo, get, size } from 'lodash'
import { getCompanySetting } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'
import { COMPANY_SETTING_HAS_EXPORT_DATA } from '@/constant/user'
import { IsAdminRole } from '@/utils/users'
import { useAuth } from '@/providers/auth.provider'
import HttpRequest from '@/config/axios.config'

interface UseExportDataProps {
  exportKey: string
  queryKey: string
  /** When false the settings query is skipped entirely (no API call is made). Defaults to true. */
  enabled?: boolean
}

interface ExportItem {
  flow: 'send_mail' | 'force_download' | string
  name: string
  title: string
  max_items?: number
  display_order?: number
}

interface UseExportDataReturn {
  exportConfigs: ExportItem[]
  exportConfig: ExportItem | null
  handleExportClick: (item?: ExportItem) => void
  handleConfirmExport: () => Promise<void>
  isExporting: boolean
  isModalOpen: boolean
  selectedItem: ExportItem | null
  closeModal: () => void
  exportError: string | null
}

export const useExportData = ({
  exportKey,
  queryKey,
  enabled = true,
}: UseExportDataProps): UseExportDataReturn => {
  const { user } = useAuth()
  const [isExporting, setIsExporting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ExportItem | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      if (event.data?.type === 'EXPORT_ERROR') {
        const errorMsg = event.data?.message || 'Export failed'
        message.error(errorMsg)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const { data: exportConfigs = [] } = useQuery(
    [queryKey, exportKey, user?.id],
    async () => {
      if (!user) return []
      const isAdmin = IsAdminRole(user)
      const setting = await getCompanySetting(
        '',
        COMPANY_SETTING_HAS_EXPORT_DATA
      )

      if (!setting) return []

      const { value = {}, status = 'inactive' } = setting ?? {}
      const exportItems: ExportItem[] = value?.item?.[exportKey] ?? []
      const isNotAllow = !size(exportItems)

      if (!isAdmin || isNotAllow || status === 'inactive') {
        return []
      }

      return exportItems
    },
    { enabled }
  )

  const exportConfig = get(exportConfigs, '[0]', null)

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedItem(null)
    setExportError(null)
  }

  const handleSendMail = (response: any) => {
    const successMessage = get(
      response,
      'data.data',
      'Export started successfully'
    )
    message.success(successMessage)
  }

  const executeExport = async (item: ExportItem) => {
    try {
      setIsExporting(true)
      setExportError(null)

      const payload = {
        name: item.name,
        flow: item.flow,
      }
      const apiEndpoint = '/export-data'
      const response = await HttpRequest.post(apiEndpoint, payload)

      handleSendMail(response)
      return true
    } catch (error: any) {
      const errorMessage =
        get(error, 'response.data.message') || get(error, 'message')
      if (errorMessage) setExportError(errorMessage)
      return false
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportClick = (item?: ExportItem) => {
    const activeItem = defaultTo(item, exportConfig)
    if (!activeItem?.name) return

    const isForceDownload = activeItem.flow === 'force_download'

    if (isForceDownload) {
      const params = new URLSearchParams({
        name: activeItem.name,
        flow: activeItem.flow,
      })
      const exportUrl = `/export-data?${params.toString()}`
      window.open(exportUrl, '_blank')
    } else {
      setSelectedItem(activeItem)
      setIsModalOpen(true)
    }
  }
  const handleConfirmExport = async () => {
    if (!selectedItem) return

    const isSuccess = await executeExport(selectedItem)
    if (isSuccess) {
      closeModal()
    }
  }

  return {
    exportConfigs,
    exportConfig,
    handleExportClick,
    handleConfirmExport,
    isExporting,
    isModalOpen,
    selectedItem,
    closeModal,
    exportError,
  }
}
